import { supabase, getCurrentUser } from './supabase';

/**
 * Create a new order
 * @param {Object} orderData - Order data
 * @returns {Promise<Object>} Operation result
 */
export const createOrder = async (orderData) => {
  try {
    // تجهيز بيانات الطلب اعتمادًا على رقم التليفون فقط
    const insertRow = {
      customer_name: orderData.customerName,
      customer_phone: orderData.customerPhone, // ← المفتاح الأساسي للربط
      pickup_address: orderData.pickupLocation,
      delivery_address: orderData.deliveryLocation || null,
      notes: orderData.notes || null,
      status: "قيد الانتظار",
      created_at: new Date().toISOString(),
    };

    // تنفيذ الإدخال في جدول orders
    const { data, error } = await supabase
      .from("orders")
      .insert([insertRow])
      .select()
      .single();

    if (error) throw error;

    console.log("✅ Order created successfully:", data);

    return {
      status: "success",
      data: data,
      message: "تم ارسال طلبك بنجاح، سيتم التواصل معك في أقرب وقت"
    };
  } catch (error) {
    console.error("❌ Error creating order:", error);
    return {
      status: "error",
      error: error.message,
    };
  }
};


/**
 * Get orders by phone number
 * @param {string} phone - Phone number
 * @returns {Promise<Object>} List of orders
 */
export const getOrdersByPhone = async (phone) => {
  try {
    if (!phone) {
      throw new Error('Phone number is required');
    }

    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('customer_phone', phone)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return {
      status: 'success',
      data: data
    };
  } catch (error) {
    console.error('Error fetching orders by phone:', error);
    return {
      status: 'error',
      error: error.message
    };
  }
};

/**
 * Update order status
 * @param {number} orderId - Order ID
 * @param {string} status - New status
 * @returns {Promise<Object>} Operation result
 */
export const updateOrderStatus = async (orderId, status) => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .update({ 
        status: status,
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId)
      .select()
      .single();

    if (error) throw error;

    return {
      status: 'success',
      data: data
    };
  } catch (error) {
    console.error('Error updating order status:', error);
    return {
      status: 'error',
      error: error.message
    };
  }
};

/**
 * Get all active orders
 * @returns {Promise<Object>} List of active orders
 */
export const getAllActiveOrders = async () => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .neq('status', 'cancelled')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return {
      status: 'success',
      data: data
    };
  } catch (error) {
    console.error('Error fetching active orders:', error);
    return {
      status: 'error',
      error: error.message
    };
  }
};

/**
 * Get user's orders by user ID
 * @param {string} userId - User ID in Supabase
 * @returns {Promise<Object>} List of user orders
 */
export const getUserOrders = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return {
      status: 'success',
      data: data
    };
  } catch (error) {
    console.error('Error fetching user orders:', error);
    return {
      status: 'error',
      error: error.message
    };
  }
};

/**
 * Move order to trash by marking it as deleted
 * @param {number} orderId - Order ID
 * @returns {Promise<Object>} Operation result
 */
export const moveOrderToTrash = async (orderId) => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .update({ 
        is_deleted: true,
        deleted_at: new Date().toISOString(),
        status: 'deleted'
      })
      .eq('id', orderId)
      .select()
      .single();

    if (error) throw error;

    return {
      status: 'success',
      data: data
    };
  } catch (error) {
    console.error('Error moving order to trash:', error);
    return {
      status: 'error',
      error: error.message
    };
  }
};

/**
 * Restore order from trash
 * @param {number} orderId - Order ID
 * @returns {Promise<Object>} Operation result
 */
export const restoreOrderFromTrash = async (orderId) => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .update({ 
        is_deleted: false,
        deleted_at: null,
        status: 'قيد الانتظار'
      })
      .eq('id', orderId)
      .select()
      .single();

    if (error) throw error;

    return {
      status: 'success',
      data: data
    };
  } catch (error) {
    console.error('Error restoring order from trash:', error);
    return {
      status: 'error',
      error: error.message
    };
  }
};

/**
 * Delete order permanently
 * @param {number} orderId - Order ID
 * @returns {Promise<Object>} Operation result
 */
export const deleteOrderPermanently = async (orderId) => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .delete()
      .eq('id', orderId)
      .select()
      .single();

    if (error) throw error;

    return {
      status: 'success',
      data: data
    };
  } catch (error) {
    console.error('Error deleting order permanently:', error);
    return {
      status: 'error',
      error: error.message
    };
  }
};

/**
 * Clean up old orders and deleted orders
 * Deletes orders that are:
 * - Marked as deleted and older than 12 hours
 * - Older than 48 hours regardless of status
 * @returns {Promise<Object>} Operation result with count of deleted orders
 */
export const cleanupOrders = async () => {
  try {
    const tweleveHoursAgo = new Date();
    tweleveHoursAgo.setHours(tweleveHoursAgo.getHours() - 12);

    const fortyEightHoursAgo = new Date();
    fortyEightHoursAgo.setHours(fortyEightHoursAgo.getHours() - 48);

    // Delete orders marked as deleted and older than 12 hours
    const { error: error1, count: count1 } = await supabase
      .from('orders')
      .delete()
      .eq('is_deleted', true)
      .lt('deleted_at', tweleveHoursAgo.toISOString());

    if (error1) throw error1;

    // Delete orders older than 48 hours
    const { error: error2, count: count2 } = await supabase
      .from('orders')
      .delete()
      .lt('created_at', fortyEightHoursAgo.toISOString());

    if (error2) throw error2;

    return {
      status: 'success',
      data: {
        deletedCount: (count1 || 0) + (count2 || 0)
      }
    };
  } catch (error) {
    console.error('Error cleaning up orders:', error);
    return {
      status: 'error',
      error: error.message
    };
  }
};

/**
 * Subscribe to real-time order updates
 * @param {Function} callback - Function to call when orders change
 * @returns {Function} Unsubscribe function
 */
export const subscribeToOrders = (callback) => {
  try {
    const subscription = supabase
      .channel('orders')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'orders' 
        }, 
        payload => {
          const data = payload.new || payload.old;
          // Format the data to match the previous Firebase format
          const formattedData = {
            id: data.id,
            ...data,
            timestamp: new Date(data.created_at),
            createdAt: { seconds: Math.floor(new Date(data.created_at).getTime() / 1000) },
            updatedAt: data.updated_at ? { seconds: Math.floor(new Date(data.updated_at).getTime() / 1000) } : null
          };
          
          callback({
            type: payload.eventType,
            data: formattedData
          });
        }
      )
      .subscribe();

    // Return unsubscribe function
    return () => {
      subscription.unsubscribe();
    };
  } catch (error) {
    console.error('Error subscribing to orders:', error);
    throw error;
  }
};

// Alias for backward compatibility
export const subscribeToNewOrders = subscribeToOrders;

/**
 * Fetch all orders
 * @returns {Promise<Object>} Operation result
 */
export const fetchOrders = async () => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Format the data to match the previous Firebase format
    const formattedData = data.map(order => ({
      id: order.id,
      ...order,
      timestamp: new Date(order.created_at),
      createdAt: { seconds: Math.floor(new Date(order.created_at).getTime() / 1000) },
      updatedAt: order.updated_at ? { seconds: Math.floor(new Date(order.updated_at).getTime() / 1000) } : null,
      orderNumber: order.order_number || `ORD-${order.id.slice(0, 6)}`,
      isDeleted: order.is_deleted || false
    }));

    return {
      status: 'success',
      data: formattedData
    };
  } catch (error) {
    console.error('Error fetching orders:', error);
    return {
      status: 'error',
      error: error.message
    };
  }
};

// Alias for backward compatibility
export const getUserOrdersByPhone = getOrdersByPhone;